import React, { useState, useEffect } from 'react';
import { Box, Button, Card, CardContent, Typography, IconButton, Fade, Collapse } from '@mui/material';
import { Close, GetApp, PhoneIphone, Computer, Android } from '@mui/icons-material';
import PWAInstallService from '../services/pwaInstallService';

interface PWAInstallBannerProps {
  onClose?: () => void;
}

const PWAInstallBanner: React.FC<PWAInstallBannerProps> = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installService] = useState(() => PWAInstallService.getInstance());
  const [platformInfo, setPlatformInfo] = useState(installService.getPlatformInfo());
  const [installMethod, setInstallMethod] = useState(installService.getInstallMethod());

  useEffect(() => {
    const checkVisibility = () => {
      const shouldShow = installService.shouldShowInstallBanner();
      setIsVisible(shouldShow);
      
      if (shouldShow) {
        installService.recordBannerShown();
      }
    };

    // Check visibility after a short delay to allow page to load
    const timer = setTimeout(checkVisibility, 3000);
    
    return () => clearTimeout(timer);
  }, [installService]);

  const handleInstall = async () => {
    if (installMethod === 'native') {
      setIsInstalling(true);
      const installed = await installService.triggerInstallPrompt();
      setIsInstalling(false);
      
      if (installed) {
        handleClose();
      }
    } else {
      setShowInstructions(true);
    }
  };

  const handleClose = (permanent: boolean = false) => {
    setIsVisible(false);
    installService.recordBannerDismissed(permanent);
    onClose?.();
  };

  const getInstallButtonText = () => {
    switch (installMethod) {
      case 'native':
        return 'Install App';
      case 'ios-safari':
        return 'Add to Home Screen';
      case 'manual':
        return 'See Instructions';
      default:
        return 'Install';
    }
  };

  const getInstallIcon = () => {
    if (platformInfo.isIOS) {
      return <PhoneIphone />;
    }
    if (platformInfo.isAndroid) {
      return <Android />;
    }
    if (platformInfo.isMobile) {
      return <PhoneIphone />;
    }
    return <Computer />;
  };

  const renderInstructions = () => {
    const instructions = installService.getInstallInstructions();
    
    return (
      <Collapse in={showInstructions}>
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 215, 0, 0.1)', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{instructions.icon}</span>
            {instructions.title}
          </Typography>
          
          {instructions.steps.map((step, index) => (
            <Typography 
              key={index} 
              variant="body2" 
              sx={{ 
                mb: 0.5, 
                pl: 2, 
                position: 'relative',
                '&::before': {
                  content: `"${index + 1}."`,
                  position: 'absolute',
                  left: 0,
                  fontWeight: 'bold',
                  color: 'primary.main'
                }
              }}
            >
              {step}
            </Typography>
          ))}

          {platformInfo.isIOS && platformInfo.isSafari && (
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(0, 0, 0, 0.1)', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                💡 Look for the Share button (square with arrow up) at the bottom of your screen
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={() => setShowInstructions(false)}
              sx={{ color: 'text.secondary' }}
            >
              Got it
            </Button>
            <Button
              size="small"
              onClick={() => handleClose(true)}
              sx={{ color: 'text.secondary' }}
            >
              Don't show again
            </Button>
          </Box>
        </Box>
      </Collapse>
    );
  };

  if (!isVisible || installMethod === 'none') {
    return null;
  }

  return (
    <Fade in={isVisible}>
      <Box
        sx={{
          position: 'fixed',
          bottom: platformInfo.isMobile ? 16 : 24,
          left: platformInfo.isMobile ? 16 : 24,
          right: platformInfo.isMobile ? 16 : 'auto',
          maxWidth: platformInfo.isMobile ? 'none' : 400,
          zIndex: 1300,
          animation: 'slideUp 0.3s ease-out',
          '@keyframes slideUp': {
            from: {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            to: {
              transform: 'translateY(0)',
              opacity: 1,
            },
          },
        }}
      >
        <Card
          elevation={8}
          sx={{
            background: 'linear-gradient(135deg, #0A0E14 0%, #1a1f2e 100%)',
            border: '1px solid #FFD700',
            borderRadius: 2,
            color: 'white',
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getInstallIcon()}
                <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                  Get the T1 FX Team App
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleClose(false)}
                sx={{ color: 'white', mt: -0.5 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
              {platformInfo.isMobile 
                ? 'Install our app for faster access, offline features, and push notifications!'
                : 'Install our desktop app for quick access and a native experience!'
              }
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleInstall}
                disabled={isInstalling}
                startIcon={isInstalling ? undefined : <GetApp />}
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#E6C200',
                  },
                  '&:disabled': {
                    backgroundColor: '#666',
                    color: '#ccc',
                  },
                }}
              >
                {isInstalling ? 'Installing...' : getInstallButtonText()}
              </Button>

              {!showInstructions && (
                <Button
                  size="small"
                  onClick={() => handleClose(true)}
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    minWidth: 'auto',
                    px: 1,
                  }}
                >
                  Not now
                </Button>
              )}
            </Box>

            {renderInstructions()}

            {/* Benefits section for mobile users */}
            {platformInfo.isMobile && !showInstructions && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255, 215, 0, 0.2)' }}>
                <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, mb: 0.5 }}>
                  ✨ App benefits:
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, fontSize: '0.7rem' }}>
                  • Faster loading • Offline access • Push notifications • Home screen icon
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

export default PWAInstallBanner; 